import { HAS_WINDOW } from './window.js';

let displayP3SupportedCached;
/**
 * Whether the browser supports CSS Color Level 4 `color()` syntax.
 *
 * When this returns `false` and the target color space is `'display-p3'`, canvas rendering
 * must use the sRGB→P3 polyfill ({@link copyCanvasSrgbToP3}) instead of `color(display-p3 ...)`
 * in `fillStyle`.
 *
 * This is needed in browsers that support canvas `{ colorSpace: 'display-p3' }` but do
 * NOT support CSS Color Level 4 `color()` syntax in `fillStyle` (e.g. Chrome 99–110).
 * In that case, gradients must be rendered to an sRGB canvas first, then the raw pixel
 * data is relabeled as display-p3 via `ImageData`.
 *
 * @see {@link https://caniuse.com/css-color-function | Can I Use: CSS color() function}
 * @see {@link https://caniuse.com/mdn-api_canvasrenderingcontext2d_fillstyle_color_function | Can I Use: fillStyle color() support}
 * @see {@link https://caniuse.com/mdn-api_htmlcanvaselement_getcontext_2d_context_options_colorspace_parameter | Can I Use: canvas colorSpace parameter}
 */
function isDisplayP3Supported() {
    return (displayP3SupportedCached ??=
        HAS_WINDOW && CSS.supports('color', 'color(display-p3 1 1 1)'));
}
/**
 * Copy sRGB canvas content to a display-p3 canvas by relabeling ImageData.
 *
 * This polyfill works by reading pixel data from an sRGB canvas and creating a new
 * `ImageData` tagged as `'display-p3'`, which the browser then interprets in the wider
 * gamut. This is necessary for browsers that support P3 canvas contexts but don't support
 * CSS Color Level 4 `color()` syntax in `fillStyle`.
 *
 * Adapted from `render2dCanvasSrgbToDisplayP3_POLYFILL` in
 * `web/js/figma_app/lib/fullscreen/color_management/canvas.ts`.
 *
 * @see {@link https://caniuse.com/mdn-api_htmlcanvaselement_getcontext_2d_context_options_colorspace_parameter | Can I Use: canvas colorSpace parameter}
 * @see {@link https://bugs.chromium.org/p/chromium/issues/detail?id=1304515 | Chromium bug: color() in fillStyle}
 */
function copyCanvasSrgbToP3(source, target) {
    const { width, height } = target.canvas;
    const srgbImage = source.getImageData(0, 0, width, height);
    const p3Image = new ImageData(srgbImage.data, width, height, { colorSpace: 'display-p3' });
    target.clearRect(0, 0, width, height);
    target.putImageData(p3Image, 0, 0);
}

export { copyCanvasSrgbToP3, isDisplayP3Supported };
//# sourceMappingURL=p3.js.map
